import { useAppSelector } from '@/redux/hooks/useAppSelector';
import {
  markersImagesOfCurrentMapDataSelector,
  markersLinesCurrentMapDataSelector,
  markersPinsOfCurrentMapDataSelector,
  markersPolygonsCurrentMapDataSelector,
  markersSurfaceOfCurrentMapDataSelector,
  markersTextOfCurrentMapDataSelector,
} from '@/redux/markers/markers.selectors';
import { MapInstance } from '@/types/map';
import { usePointToProjection } from '@/utils/map/usePointToProjection';
import { getMarkersFeatures } from '@/components/Map/MapViewer/utils/config/pinsLayer/getMarkersFeatures';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import VectorSource from 'ol/source/Vector';
import { useEffect, useMemo } from 'react';
import { getMarkerImageFeatures } from './getMarkerImageFeatures';
import { getMarkerLineFeatures } from './getMarkerLineFeatures';
import { getMarkerPolygonFeatures } from './getMarkerPolygonFeatures';
import { getMarkerSurfaceFeatures } from './getMarkerSurfaceFeatures';
import { getMarkerTextFeatures } from './getMarkerTextFeatures';

/**
 * Builds the shared {@link VectorSource} containing every plugin-registered
 * marker feature (pin, surface, line, polygon, text, image) derived from the
 * Redux store. This is the single place marker features are constructed —
 * see `createMarkersLayer` to wrap this source in a layer for a given map.
 */
export const useOlMapMarkersSource = ({
  mapInstance,
}: {
  mapInstance: MapInstance;
}): VectorSource<Feature<Geometry>> => {
  const pointToProjection = usePointToProjection();

  const pins = useAppSelector(markersPinsOfCurrentMapDataSelector);
  const surfaces = useAppSelector(markersSurfaceOfCurrentMapDataSelector);
  const lines = useAppSelector(markersLinesCurrentMapDataSelector);
  const polygons = useAppSelector(markersPolygonsCurrentMapDataSelector);
  const texts = useAppSelector(markersTextOfCurrentMapDataSelector);
  const images = useAppSelector(markersImagesOfCurrentMapDataSelector);

  const features = useMemo(
    () => [
      ...getMarkersFeatures(pins, { pointToProjection }),
      ...getMarkerSurfaceFeatures(surfaces, { pointToProjection, mapInstance }),
      ...getMarkerLineFeatures(lines, { pointToProjection }),
      ...getMarkerPolygonFeatures(polygons, { pointToProjection }),
      ...getMarkerTextFeatures(texts, { pointToProjection }),
      ...getMarkerImageFeatures(images, { pointToProjection }),
    ],
    [pins, surfaces, lines, polygons, texts, images, pointToProjection, mapInstance],
  );

  const vectorSource = useMemo(() => new VectorSource<Feature<Geometry>>(), []);

  useEffect(() => {
    vectorSource.clear();
    vectorSource.addFeatures(features);
  }, [features, vectorSource]);

  return vectorSource;
};
